import { defineComponent, reactive, SetupContext, readonly, onMounted, ref, nextTick, computed } from '@vue/composition-api';
import HttpStatus from 'http-status-codes';
import CommonMessageDialog from '@/components/common/common-message-dialog.component.vue';
import CommonFunction from '@/shared/common-function';
import MessageConst from '@/shared/message-const';
import vueFilePond from 'vue-filepond';
import FilePondPluginGetFile from 'filepond-plugin-get-file';
import { FileOrigin, FilePondFile } from 'filepond';
import 'filepond/dist/filepond.min.css';
import 'filepond-plugin-get-file/dist/filepond-plugin-get-file.min.css';
import { MaterialDbAddType, MaterialDbFile, PullDown } from '@/shared/type';
import SystemConst from '@/shared/system-const';
import EmailValidator from 'email-validator';
import vueRouter from '@/router/index';

// リアクティブな変数の型定義
export type reactiveData = {
    windowSize: { x: number; y: number };   // Windowサイズ
    tab: any;                               // タブ
    isAddMode: boolean;                     // 登録・編集処理フラグ
    material: MaterialDbAddType;            // 教材
    allSchoolTypes: {
        id: number,
        name: string,
        grades: [],
        curriculums: [],
    }[],                                    // 全校種データ
    schoolTypeRadioButton: {
        value: number,
        text: string
    }[];                                    // 校種ラジオボタン
    selectGradeItems: Array<string>;        // 選択済学年
    gradeItems: [],                         // 学年ツリービュー
    selectSubjectItems: Array<string>;      // 選択済教科・科目
    subjectItems: [],                       // 教科・科目ツリービュー
    organizationPulldown: PullDown[];       // 所属プルダウン
    validateValues: ValidateType;           // FilePondのバリデーションデータ
    gradeErrorMessage: string;              // 学年のバリデーションメッセージ文字列
    isGradeValid: boolean;                  // 学年のバリデーション結果
    subjectErrorMessage: string;            // 教科・科目のバリデーションメッセージ文字列
    isSubjectValid: boolean;                // 教科・科目のバリデーション結果
    materialFileErrorMessage: string;       // 教材ファイルのバリデーションメッセージ文字列
    isMaterialFileValid: boolean;           // 教材ファイルのバリデーション結果
    thumbnailFileErrorMessage: string;      // サムネイルファイルのバリデーションメッセージ文字列
    isThumbnailFileValid: boolean;          // サムネイルファイルのバリデーション結果
    isClear: boolean;                       // 入力欄のクリア中
}

// バリデーション用パラメーター
interface ValidateType {
    [key: string]: ValidateValues
}

// バリデーション用パラメーターデータ
type ValidateValues = {
    parameterName: string;                  // パラメーター名
    value: string;                          // パラメーター値
}

// ファイルアップロード
const FilePond = vueFilePond(FilePondPluginGetFile);

/**
 * 教材DB登録画面コンポーネント
 */
export default defineComponent({
    components: { CommonMessageDialog, FilePond },
    setup(props, ctx: SetupContext) {
        const commonRef = ref<any>(null);                                   // メッセージダイアログ
        const router = vueRouter;                                           // ルータ
        const form = ref<any>(null);                                        // 入力フォーム（基本情報タブ）
        const formOption = ref<any>(null);                                  // 入力フォーム（任意情報タブ）
        const materialPondRef = ref<any>(null);                             // 教材ファイルアップロードコンポーネント
        const thumbnailPondRef = ref<any>(null);                            // サムネイルファイルアップロードコンポーネント
        const gradeTreeView = ref<any>(null);                               // 学年ツリービュー
        const subjectTreeView = ref<any>(null);                             // 教科・科目ツリービュー
        const tabBasic = "1";                                               // タブ定義
        const tabOption = "2";
        let materialId = '';                                                // 編集対象の教材ID
        // リアクティブな変数定義
        const state: reactiveData = reactive<reactiveData>({
            windowSize: { x: 0, y: 0 },                                     // ウィンドウサイズ
            tab: '',                                                        // タブ
            isAddMode: true,                                                // 登録・編集処理フラグ
            material: {                                                     // 教材
                title: '',                                                  // タイトル
                urls: '',                                                   // 教材URL
                materialFiles: [],                                          // 教材ファイル
                schoolTypeId: 0,                                            // 校種ID
                gradeId: [],                                                // 学年ID
                curriculumId: [],                                           // 教科ID
                subject: [],                                                // 科目
                name: '',                                                   // 氏名
                publicationRangeCode:
                    SystemConst.MATERIAL_PUBLICATION_RANGE['FOR_TEACHER'],  // 公開範囲
                useTermsCode: undefined,                                    // 利用条件
                summary: '',                                                // 概要
                thumbnailFiles: [],                                         // サムネイル
                organizationId: '',                                         // 所属ID
                mail: '',                                                   // メールアドレス
                searchKeyword: '',                                          // 検索キーワード
                copyright: '',                                              // 著作
                operationEnvironment: '',                                   // 動作環境
                remarks: ''                                                 // 備考
            },
            allSchoolTypes: [],                                             // 全校種データ
            schoolTypeRadioButton: [],                                      // 校種ラジオボタン
            selectGradeItems: [],                                           // 選択済学年
            gradeItems: [],                                                 // 学年ツリービュー
            selectSubjectItems: [],                                         // 選択済教科・科目
            subjectItems: [],                                               // 教科・科目ツリービュー
            organizationPulldown: [],                                       // 所属プルダウン
            validateValues: {},                                             // FilePondのバリデーションデータ
            gradeErrorMessage: '',                                          // 学年のバリデーションメッセージ文字列
            isGradeValid: true,                                             // 学年のバリデーション結果
            subjectErrorMessage: '',                                        // 教科・科目のバリデーションメッセージ文字列
            isSubjectValid: true,                                           // 教科・科目のバリデーション結果
            materialFileErrorMessage: '',                                   // 教材ファイルのバリデーションメッセージ文字列
            isMaterialFileValid: true,                                      // 教材ファイルのバリデーション結果
            thumbnailFileErrorMessage: '',                                  // サムネイルファイルのバリデーションメッセージ文字列
            isThumbnailFileValid: true,                                     // サムネイルファイルのバリデーション結果
            isClear: false                                                  // 入力欄のクリア中
        });

        // バリデーション用最大桁数
        const maxLength = {
            title: SystemConst.MATERIAL_DB_MAX_LENGTH['TITLE'],                                     // タイトル
            url: SystemConst.MATERIAL_DB_MAX_LENGTH['URL'],                                         // URL
            name: SystemConst.MATERIAL_DB_MAX_LENGTH['NAME'],                                       // 氏名
            summary: SystemConst.MATERIAL_DB_MAX_LENGTH['SUMMARY'],                                 // 概要
            mail: SystemConst.MATERIAL_DB_MAX_LENGTH['MAIL'],                                       // メールアドレス
            searchKeyword: SystemConst.MATERIAL_DB_MAX_LENGTH['SEARCH_KEYWORD'],                    // 検索キーワード
            copyright: SystemConst.MATERIAL_DB_MAX_LENGTH['COPYRIGHT'],                             // 著作
            operationEnvironment: SystemConst.MATERIAL_DB_MAX_LENGTH['OPERATION_ENVIRONMENT'],      // 動作環境
            remarks: SystemConst.MATERIAL_DB_MAX_LENGTH['REMARKS']                                  // 備考
        }

        // タイトルのバリデーション
        const titleRules: any[] = [
            // 必須チェック
            (v: any) => !!v || CommonFunction.replaceArray(MessageConst.E_REQUIRED, ['タイトル']),
            // 桁数チェック
            (v: any) => CommonFunction.checkLength(v, maxLength['title'], 'タイトル')
        ];

        // 教材URLのバリデーション
        const urlRules: any[] = [
            // 桁数チェック
            (v: any) => CommonFunction.checkLength(v, maxLength['url'], '教材URL'),
            // 必須チェック（URLまたは教材ファイルの何れか必須）
            (v: any) => {
                // 教材ファイルの一覧をFilePondから得る
                const files: FilePondFile[] = materialPondRef.value.getFiles();
                // 何れも未入力の場合
                if (v !== null && v.length === 0 && files.length === 0) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_INPUT_SELECT_AT_LEAST_ONE, ['教材URL', '教材ファイル']
                    )
                }
                // 教材ファイルのバリデーションを行い、正常であれば教材ファイルのエラーメッセージを消す
                updateMaterialFiles();
            },
            // 属性チェック（http:// or https:// から始まる）
            (v: any) => {
                if (v === null || v === '') {
                    return true;
                }
                // URLの最終行で改行した場合は削除する
                if (String(v).slice(-1) === '\n') {
                    v = v.slice(0, v.length - 1);
                }
                const parts = String(v).split('\n');
                for (const key in parts) {
                    // 長さ0以上の各文字列に対してチェックする
                    if (!parts[key].startsWith(SystemConst.URL_SCHEME['HTTP']) &&
                        !parts[key].startsWith(SystemConst.URL_SCHEME['HTTPS'])) {
                        // URLはhttp://またはhttps://から始まる文字列で入力してください。
                        return CommonFunction.replaceArray(
                            MessageConst.E_URL_SCHEME_ERR, [SystemConst.URL_SCHEME['HTTP'], SystemConst.URL_SCHEME['HTTPS']])
                    }
                }
                return true;
            },
            // 重複チェック（URLが重複）
            (v: any) => {
                if (v === null || v === '') {
                    return true;
                }
                const parts = String(v).split('\n');
                if (Array.from(new Set(parts.map(e => e))).length !== parts.length) {
                    // 重複を排除した配列の長さが異なれば、重複あり
                    return MessageConst.E_URL_DUPLICATE;
                }
                return true;
            }
        ];

        // 教材ファイルのバリデーション
        const materialFileRules: any[] = [
            // 必須チェック（URLまたは教材ファイルの何れか必須）
            () => {
                // 教材ファイルの一覧をFilePondから得る
                const files: FilePondFile[] = materialPondRef.value.getFiles();
                // 教材URLを得る
                const url = state.material.urls;
                // 何れも未入力の場合
                if (url === null && files.length === 0) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_INPUT_SELECT_AT_LEAST_ONE, ['教材URL', '教材ファイル']
                    )
                }
                return true;
            },
            // 属性チェック（ファイル数、拡張子、ファイルサイズ（0バイトより大きく、上限以下）
            () => {
                // 教材ファイルの一覧をFilePondから得る
                const files: FilePondFile[] = materialPondRef.value.getFiles();
                // バリデーション値
                const maxCount = Number(state.validateValues['materialFileCount'].value);
                // ファイル数チェック
                return filePondFileCount(0, maxCount, '教材', files);
            },
            () => {
                // 教材ファイルの一覧をFilePondから得る
                const files: FilePondFile[] = materialPondRef.value.getFiles();
                // バリデーション値
                const allowExtension = state.validateValues['materialFileExtension'].value.split(',');
                // 拡張子チェック
                return filePondExtension(allowExtension, files);
            },
            () => {
                // 教材ファイルの一覧をFilePondから得る
                const files: FilePondFile[] = materialPondRef.value.getFiles();
                // バリデーション値
                const maxSize = Number(state.validateValues['materialFileSize'].value);
                // ファイルサイズチェック
                return filePondFileSize(maxSize, files);
            },
            // 重複チェック（ファイル名が重複）
            () => {
                // 教材ファイルの一覧をFilePondから得る
                const files: FilePondFile[] = materialPondRef.value.getFiles();
                // ファイル名重複チェック
                return filePondDuplicate(files);
            }
        ];

        // 学年のバリデーション
        const gradeRules: any[] = [
            // 必須チェック（ツリーが存在する場合のみチェック）
            () => state.gradeItems.length === 0 || state.selectGradeItems.length !== 0 ||
                CommonFunction.replaceArray(MessageConst.E_UNSELECTED, ['学年'])
        ];

        // 教科・科目のバリデーション
        const subjectRules: any[] = [
            // 必須チェック（ツリーが存在する場合のみチェック）
            () => state.subjectItems.length === 0 || state.selectSubjectItems.length !== 0 ||
                CommonFunction.replaceArray(MessageConst.E_UNSELECTED, ['教科・科目'])
        ];

        // 氏名（代表者）のバリデーション
        const nameRules: any[] = [
            // 桁数チェック
            (v: any) => CommonFunction.checkLength(v, maxLength['name'], '氏名（代表者）'),
            // 必須チェック
            (v: any) => !!v || CommonFunction.replaceArray(MessageConst.E_REQUIRED, ['氏名（代表者）'])
        ];

        // 概要のバリデーション
        const summaryRules: any[] = [
            // 桁数チェック
            (v: any) => CommonFunction.checkLength(v, maxLength['summary'], '概要')
        ];

        // サムネイルのバリデーション
        const thumbnailFileRules: any[] = [
            // 属性チェック（ファイル数、拡張子、ファイルサイズ（0バイトより大きく、上限以下）
            () => {
                // サムネイルファイルの一覧をFilePondから得る
                const files: FilePondFile[] = thumbnailPondRef.value.getFiles();
                // バリデーション値
                const maxCount = Number(state.validateValues['thumbnailFileCount'].value);
                // ファイル数チェック
                return filePondFileCount(0, maxCount, 'サムネイル', files);
            },
            () => {
                // サムネイルファイルの一覧をFilePondから得る
                const files: FilePondFile[] = thumbnailPondRef.value.getFiles();
                // バリデーション値
                const allowExtension = state.validateValues['thumbnailFileExtension'].value.split(',');
                // 拡張子チェック
                return filePondExtension(allowExtension, files);
            },
            () => {
                // サムネイルファイルの一覧をFilePondから得る
                const files: FilePondFile[] = thumbnailPondRef.value.getFiles();
                // バリデーション値
                const maxSize = Number(state.validateValues['thumbnailFileSize'].value);
                // ファイルサイズチェック
                return filePondFileSize(maxSize, files);
            },
            // 重複チェック（ファイル名が重複）
            () => {
                // サムネイルファイルの一覧をFilePondから得る
                const files: FilePondFile[] = thumbnailPondRef.value.getFiles();
                // ファイル名重複チェック
                return filePondDuplicate(files);
            }
        ];

        // メールアドレスのバリデーション
        const mailRules: any[] = [
            // 桁数チェック
            (v: any) => CommonFunction.checkLength(v, maxLength['mail'], 'メールアドレス'),
            // 属性チェック
            (v: any) => /^[0-9a-z-_'.@]*$/.test(v) ||
                CommonFunction.replaceArray(MessageConst.E_REGEX, ['メールアドレス', '小文字の英字、数字、ピリオド（.）、ハイフン（-）のみ']),
            (v: any) =>
                !v || EmailValidator.validate(v) ||
                CommonFunction.replaceArray(MessageConst.E_EMAIL, ['メールアドレス'])
        ];

        // 検索キーワードのバリデーション
        const searchKeywordRules: any[] = [
            // 桁数チェック
            (v: any) => CommonFunction.checkLength(v, maxLength['searchKeyword'], '検索キーワード')
        ];

        // 著作のバリデーション
        const copyrightRules: any[] = [
            // 桁数チェック
            (v: any) => CommonFunction.checkLength(v, maxLength['copyright'], '著作')
        ];

        // 動作環境のバリデーション
        const operationEnvironmentRules: any[] = [
            // 桁数チェック
            (v: any) => CommonFunction.checkLength(v, maxLength['operationEnvironment'], '動作環境')
        ];

        // 備考のバリデーション
        const remarksRules: any[] = [
            // 桁数チェック
            (v: any) => CommonFunction.checkLength(v, maxLength['remarks'], '備考')
        ];

        // 所属のバリデーション
        const organizationIdRules: any[] = [
            // 必須チェック
            (v: any) => !!v && state.organizationPulldown.indexOf(v[0]) === -1 ||
                CommonFunction.replaceArray(MessageConst.E_UNSELECTED, ['所属'])
        ];

        // FilePondバリデーション
        function filePondFileCount(min: number, max: number, messagePrefix: string, files: FilePondFile[]) {
            // ファイル数が下限より大きい
            if (files.length < min) {
                return MessageConst.E_FILE_REQUIRED;
            }
            // ファイル数が上限以下
            if (files.length > max) {
                return CommonFunction.replaceArray(
                    MessageConst.E_FILE_FILE_COUNT, [
                    messagePrefix,
                    max + 'ファイル以下']);
            }
            return true;
        }

        // FilePondバリデーション
        function filePondFileSize(max: number, files: FilePondFile[]) {
            // ファイルサイズが0バイトより大きく、上限以下であるか確認する
            for (const key in files) {
                if (files[key].fileSize > max) {
                    // 単位をメッセージの単位"KB"に合わせるため、fileSizeを1024で割る
                    return CommonFunction.replaceArray(
                        MessageConst.E_FILE_MAX_SIZE, [max / 1024]);
                } else if (files[key].fileSize === 0) {
                    return MessageConst.E_FILE_NO_DATA;
                }
            }
            return true;
        }

        // FilePondバリデーション
        function filePondExtension(extension: string[], files: FilePondFile[]) {
            // 拡張子が登録対象か確認する
            for (const key in files) {
                // 登録しようとするファイルの拡張子を得る
                const parts = files[key].file.name.split(/\.(?=[^.]+$)/);
                const fileExtension = parts[1];

                if (extension.indexOf(fileExtension) === -1) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_FILE_EXTENSION, [extension.join(',')]);
                }
            }
            return true;
        }

        // FilePondバリデーション
        function filePondDuplicate(files: FilePondFile[]) {
            // ファイル名が重複していないか確認する
            if (Array.from(new Set(files.map(e => e.file.name))).length !== files.length) {
                // 重複を排除した配列の長さが異なれば、重複あり
                return MessageConst.E_FILE_NAME_DUPLICATE;
            }
            return true;
        }

        /**
         * 初期処理
         */
        onMounted(() => {
            init();
        });
        async function init() {
            // 教材登録初期表示取得APIで初期表示データを取得する
            const response = await ctx.root.$store.dispatch('materialDb/getAddParameters');

            // HTTPステータスコードが200以外の場合、エラー画面へ遷移して処理を終了する
            if (response.status !== HttpStatus.OK) {
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // 教材ファイルとサムネイルのバリデーション値を設定する
            state.validateValues = response.data.parameters;

            // 全校種データを取得
            state.allSchoolTypes = response.data.schoolTypes;

            // 校種をラジオボタンへ設定する
            const radioButtonValues = state.allSchoolTypes.map((v: { id: any; name: any; }) => {
                // id=>value, name=>textに変更
                return { value: v.id, text: v.name }
            });
            state.schoolTypeRadioButton = radioButtonValues;

            // 初期表示する校種の教科・科目をツリービューへ設定する
            state.gradeItems = state.allSchoolTypes[0].grades;
            state.subjectItems = state.allSchoolTypes[0].curriculums;

            // 氏名（代表者）をストア（認証情報）から取得して設定する
            state.material.name = ctx.root.$store.state.authenticationUser.userName;

            // 所属プルダウンを設定する
            state.organizationPulldown = response.data.organizationPulldown;

            // 教材編集画面の場合
            if (!state.isAddMode) {
                //   教材更新/削除対象取得APIで編集データを取得する
                //   パラメーター：教材ID
                const responseMaterial = await ctx.root.$store.dispatch('materialDb/getOperateMaterial',
                    {
                        materialId: materialId
                    }
                );

                // 通信結果判別
                if (responseMaterial.status !== HttpStatus.OK) {
                    if (responseMaterial.status === HttpStatus.NOT_FOUND) {
                        // HTTPステータスコードが404の場合
                        // displayErrorMessageTransitionDialogメソッドでエラーダイアログを表示し、教材一覧画面へ遷移する
                        commonRef.value.displayErrorMessageTransitionDialog(responseMaterial, '/material-db/list');
                        return;
                    }
                    // HTTPステータスコードが200, 404以外の場合
                    // errorDisplayTransitionControlメソッドでエラー画面を表示する
                    commonRef.value.errorDisplayTransitionControl(responseMaterial);
                    return;
                }
                // 画面項目に編集データを設定する
                state.material = responseMaterial.data.material;
                // 校種の選択イベントにより学年と教科・科目ツリーを表示
                await changeSchoolType();
                // 学年を設定
                state.selectGradeItems = responseMaterial.data.material.gradeIds;
                // 教科・科目を設定
                state.selectSubjectItems = responseMaterial.data.material.curriculumIds;

                // 教材ファイル・サムネイル
                responseMaterial.data.material.materialFiles.forEach((e: { fileName: string; fileSize: number; fileSequence: number }) => {
                    materialPondRef.value.addFile(e.fileName, {
                        type: 'local',              // 'local':サーバーにあるファイル 'input':ユーザが追加したファイル
                        file: {
                            name: e.fileName,       // ファイル名
                            size: e.fileSize        // ファイルサイズ
                        },
                        metadata: {
                            url: `/api/material-db/${materialId}/${e.fileSequence}`   // ダウンロード時のURL
                        }
                    });
                });
                responseMaterial.data.material.thumbnailFiles.forEach((e: { fileName: string; fileSize: number; fileSequence: number }) => {
                    thumbnailPondRef.value.addFile(e.fileName, {
                        type: 'local',              // 'local':サーバーにあるファイル 'input':ユーザが追加したファイル
                        file: {
                            name: e.fileName,       // ファイル名
                            size: e.fileSize        // ファイルサイズ
                        },
                        metadata: {
                            url: `/api/material-db/thumbnail/${materialId}/${e.fileSequence}`   // ダウンロード時のURL
                        }
                    });
                });
            }
        }

        /**
         * 初期表示
         */
        created();
        async function created() {
            // 編集対象の教材IDを遷移元から取得して、materialIdへ設定
            const id = computed(() => ctx.root.$route.params.id);

            if (typeof id.value !== 'undefined') {
                // 編集対象の教材IDが格納されている場合
                //   isAddModeをfalseに変更する
                state.isAddMode = false;

                // 教材IDを保存
                materialId = id.value
            }

            // 登録処理の判定
            if (state.isAddMode) {
                // 登録処理の場合
                // titleIcon変更
                ctx.root.$store.commit('setTitleIcon', 'mdi-database-plus');
                // mainTitleを「教材登録」に変更
                ctx.root.$store.commit('setMainTitle', '教材登録');
            } else {
                // 編集処理の場合
                // titleIcon変更
                ctx.root.$store.commit('setTitleIcon', 'mdi-database-check');
                // mainTitleを「教材編集」に変更
                ctx.root.$store.commit('setMainTitle', '教材編集');
            }
        }

        /**
         * 対象校種ラジオボタンの選択イベント
         */
        async function changeSchoolType() {
            // 現在のチェック状態を解除
            state.selectGradeItems = [];
            state.selectSubjectItems = [];

            // チェック状態がDOMへ反映された後に、ツリービューを入れ替える
            nextTick(() => {
                // ラジオボタンで選択された校種のindex取得
                const initIndex = state.schoolTypeRadioButton.findIndex(
                    x => x.value === state.material.schoolTypeId);

                // 選択した校種の教科・科目をツリービューへ設定する
                state.gradeItems = state.allSchoolTypes[initIndex].grades;
                state.subjectItems = state.allSchoolTypes[initIndex].curriculums;

                // ツリービューを閉じる
                gradeTreeView.value.updateAll(false);
                subjectTreeView.value.updateAll(false);
            })
        }

        /**
         * 学年の選択イベント
         */
        function changeGradeItems() {
            // 学年のバリデーションを行う
            for (let index = 0; index < gradeRules.length; index++) {
                const rule: any = gradeRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // エラーあり（メッセージ表示）
                    state.gradeErrorMessage = valid;
                    state.isGradeValid = false;
                    break;
                } else {
                    // エラーなし（メッセージ消去）
                    state.gradeErrorMessage = '';
                    state.isGradeValid = true;
                }
            }
        }

        /**
         * 教科・科目の選択イベント
         */
        function changeSubjectItems() {
            // 教科・科目のバリデーションを行う
            for (let index = 0; index < subjectRules.length; index++) {
                const rule: any = subjectRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // エラーあり（メッセージ表示）
                    state.subjectErrorMessage = valid;
                    state.isSubjectValid = false;
                    break;
                } else {
                    // エラーなし（メッセージ消去）
                    state.subjectErrorMessage = '';
                    state.isSubjectValid = true;
                }
            }
        }

        /**
         * 教材ファイルへファイルを追加・削除された場合のイベント
         */
        function updateMaterialFiles() {
            // 教材ファイルのバリデーションを行う
            for (let index = 0; index < materialFileRules.length; index++) {
                const rule: any = materialFileRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // クリア中はバリデーションを実施しない
                    if (state.isClear) {
                        state.isClear = false;
                        break;
                    }
                    // エラーあり（メッセージ表示）
                    state.materialFileErrorMessage = valid;
                    state.isMaterialFileValid = false;
                    break;
                } else {
                    // エラーなし（メッセージ消去）
                    state.materialFileErrorMessage = '';
                    state.isMaterialFileValid = true;
                }
            }
        }

        /**
         * サムネイルへファイルを追加・削除された場合のイベント
         */
        function updateThumbnailFiles() {
            // サムネイルのバリデーションを行う
            for (let index = 0; index < thumbnailFileRules.length; index++) {
                const rule: any = thumbnailFileRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // クリア中はバリデーションを実施しない
                    if (state.isClear) {
                        state.isClear = false;
                        break;
                    }
                    // エラーあり（メッセージ表示）
                    state.thumbnailFileErrorMessage = valid;
                    state.isThumbnailFileValid = false;
                    break;
                } else {
                    // エラーなし（メッセージ消去）
                    state.thumbnailFileErrorMessage = '';
                    state.isThumbnailFileValid = true;
                }
            }
        }

        /**
         * 登録確認ダイアログ表示
         */
        function displayDialog() {
            // バリデーションを行う
            //   ・基本情報フォームのバリデーション
            //   ・教材ファイルのバリデーション
            //   ・ツリービューのバリデーション
            updateMaterialFiles();
            changeGradeItems();
            changeSubjectItems();
            if (!form.value.validate() ||
                !state.isMaterialFileValid ||
                !state.isGradeValid ||
                !state.isSubjectValid) {
                // 基本情報タブへ移動
                state.tab = tabBasic;
                return;
            }

            //   ・任意情報フォームのバリデーション
            //   ・サムネイルファイルのバリデーション
            updateThumbnailFiles();
            if (!formOption.value.validate() ||
                !state.isThumbnailFileValid) {
                // 任意情報タブへ移動
                state.tab = tabOption;
                return;
            }

            let displayMessage = '';
            // 登録処理の判定
            if (state.isAddMode) {
                // 登録処理の場合
                // 登録処理実行確認ダイアログに表示するメッセージを格納する
                displayMessage = CommonFunction.replaceArray(MessageConst.S_CONFIRMATION, ['登録']);
            } else {
                // 編集処理の場合
                // 編集処理実行確認ダイアログに表示するメッセージを格納する
                displayMessage = CommonFunction.replaceArray(MessageConst.S_CONFIRMATION, ['編集']);
            }

            // 確認メッセージダイアログ表示
            commonRef.value.displayConfirmationMessageDialog(displayMessage);
        }

        /**
         * 確認メッセージダイアログの「はい」押下時の処理
         */
        async function processingData() {
            let response: any;

            // 登録処理・編集処理の判断
            if (state.isAddMode) {
                // 登録処理の場合
                // 教材ファイルをFilePondから取得
                const materialFiles: File[]
                    = materialPondRef.value.getFiles().map((e: FilePondFile) => e.file);
                // サムネイルファイルをFilePondから取得
                const thumbnailFiles: File[]
                    = thumbnailPondRef.value.getFiles().map((e: FilePondFile) => e.file);

                // 教材登録APIでデータを登録する
                // POST /api/material-db
                response = await ctx.root.$store.dispatch(
                    'materialDb/addMaterial',
                    {
                        material: {
                            'title': state.material.title,
                            'urls': state.material.urls,
                            'schoolTypeId': state.material.schoolTypeId,
                            'gradeIds': state.selectGradeItems,
                            'curriculumIds': state.selectSubjectItems,
                            'name': state.material.name,
                            'publicationRangeCode': state.material.publicationRangeCode,
                            'useTermsCode': state.material.useTermsCode,
                            'summary': state.material.summary,
                            'organizationId': state.material.organizationId,
                            'mail': state.material.mail,
                            'searchKeyword': state.material.searchKeyword,
                            'copyright': state.material.copyright,
                            'operationEnvironment': state.material.operationEnvironment,
                            'remarks': state.material.remarks
                        },
                        materialFiles: materialFiles,
                        thumbnailFiles: thumbnailFiles
                    }
                );


            } else {
                // 編集処理の場合
                const materialFiles: FilePondFile[] = materialPondRef.value.getFiles();
                const thumbnailFiles: FilePondFile[] = thumbnailPondRef.value.getFiles();

                // state.material.materialFiles（DB上のファイル）が、materialFiles（アップロードするファイル）
                // にないものはstateから消す
                let newFiles: MaterialDbFile[] = [];
                state.material.materialFiles.forEach(e => {
                    if (materialFiles.filter(x => x.file.name === e.fileName).length !== 0) {
                        newFiles.push(e);
                    }
                });
                state.material.materialFiles = newFiles;

                // state.material.thumbnailFiles（DB上のファイル）が、thumbnailFiles（アップロードするファイル）
                // にないものはstateから消す
                newFiles = [];
                state.material.thumbnailFiles.forEach(e => {
                    if (thumbnailFiles.filter(x => x.file.name === e.fileName).length !== 0) {
                        newFiles.push(e);
                    }
                });
                state.material.thumbnailFiles = newFiles;

                // materialFiles（アップロードするファイル）をstate.material.materialFilesへ追加する。
                // ただし、同じファイル名の場合は、更新された（入れ替えた）ファイルと判断し、
                // ファイルサイズのみコピーし、ファイルシーケンスを削除する。
                materialFiles.forEach(e => {
                    if (e.origin === FileOrigin.INPUT) {   // 'INPUT' = ユーザ入力のファイル
                        if (state.material.materialFiles.filter(x => x.fileName === e.file.name).length !== 0) {
                            const r = state.material.materialFiles.find(x => x.fileName === e.file.name);
                            if (r) {
                                // ファイルサイズを更新・ファイルシーケンスを削除
                                r.fileSize = e.fileSize;
                                r.fileSequence = undefined;
                            }
                        } else {
                            // DBに無い、新しいファイル
                            state.material.materialFiles.push({
                                'fileName': e.file.name,
                                'fileSize': e.fileSize
                            });
                        }
                    }
                });
                // thumbnailFiles（アップロードするファイル）をstate.material.thumbnailFilesへ追加する。
                // ただし、同じファイル名の場合は、更新された（入れ替えた）ファイルと判断し、
                // ファイルサイズのみコピーし、ファイルシーケンスを削除する。
                thumbnailFiles.forEach(e => {
                    if (e.origin === FileOrigin.INPUT) {   // 'INPUT' = ユーザ入力のファイル
                        if (state.material.thumbnailFiles.filter(x => x.fileName === e.file.name).length !== 0) {
                            const r = state.material.thumbnailFiles.find(x => x.fileName === e.file.name);
                            if (r) {
                                // ファイルサイズを更新・ファイルシーケンスを削除
                                r.fileSize = e.fileSize;
                                r.fileSequence = undefined;
                            }
                        } else {
                            // DBに無い、新しいファイル
                            state.material.thumbnailFiles.push({
                                'fileName': e.file.name,
                                'fileSize': e.fileSize
                            });
                        }
                    }
                });
                // APIへ渡すためFilePondFileオブジェクトからFileオブジェクトを作る
                const materialRealFiles: File[] = materialPondRef.value.getFiles().map((e: FilePondFile) => e.file);
                const thumbnailRealFiles: File[] = thumbnailPondRef.value.getFiles().map((e: FilePondFile) => e.file);

                // 教材編集APIで教材を編集する
                // POST /api/material-db/{materialId}
                //  materialId : materialId
                response = await ctx.root.$store.dispatch(
                    'materialDb/updateMaterial',
                    {
                        materialId: materialId,
                        material: {
                            'title': state.material.title,
                            'urls': state.material.urls,
                            'schoolTypeId': state.material.schoolTypeId,
                            'gradeIds': state.selectGradeItems,
                            'curriculumIds': state.selectSubjectItems,
                            'organizationId': state.material.organizationId,
                            'name': state.material.name,
                            'publicationRangeCode': state.material.publicationRangeCode,
                            'useTermsCode': state.material.useTermsCode,
                            'summary': state.material.summary,
                            'mail': state.material.mail,
                            'searchKeyword': state.material.searchKeyword,
                            'copyright': state.material.copyright,
                            'operationEnvironment': state.material.operationEnvironment,
                            'remarks': state.material.remarks,
                            'materialFiles': state.material.materialFiles,
                            'thumbnailFiles': state.material.thumbnailFiles
                        },
                        materialFiles: materialRealFiles,
                        thumbnailFiles: thumbnailRealFiles
                    }
                );
            }

            // HTTPステータスコードが200以外の場合
            // エラー画面へ遷移して処理を終了する
            if (response.status !== HttpStatus.OK) {
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            if (state.isAddMode) {
                // 登録処理の場合
                // HTTPステータスコードが200の場合
                // 登録完了メッセージダイアログ表示
                commonRef.value.displayMessageWithProcessDialog(response);
            } else {
                // 編集処理の場合
                // HTTPステータスコードが200の場合
                // 登録完了メッセージダイアログ表示して、教材一覧画面に戻る
                commonRef.value.displayMessageTransitionDialog(response, '/material-db/list');
            }
        }

        /**
         * 教材登録画面の登録完了メッセージで「閉じる」ボタン押下時の処理
         */
        async function clearData() {

            // クリア中はFilePondのバリデーションを実施しない
            state.isClear = true;

            // ダイアログを閉じる
            commonRef.value.close();

            // 以下を行い、初期表示と同じ状態へ戻す
            //   入力項目をクリアする
            state.material.title = '';
            state.material.urls = '';
            state.material.materialFiles = [];
            materialPondRef.value.removeFiles({ revert: true });
            state.material.schoolTypeId = state.schoolTypeRadioButton[0].value;
            state.selectGradeItems = [];
            state.selectSubjectItems = [];
            state.material.name = '';
            state.material.publicationRangeCode = SystemConst.MATERIAL_PUBLICATION_RANGE['FOR_TEACHER'];
            state.material.useTermsCode = undefined;
            state.material.summary = '';
            state.material.thumbnailFiles = [];
            thumbnailPondRef.value.removeFiles({ revert: true });
            state.material.organizationId = '';
            state.material.mail = '';
            state.material.searchKeyword = '';
            state.material.copyright = '';
            state.material.operationEnvironment = '';
            state.material.remarks = '';

            //   init()をコールし、バリデーション値・ツリービュー・氏名（代表者）・所属プルダウンを設定する
            init();

            // バリデーションをリセットする
            form.value.resetValidation();
            formOption.value.resetValidation();

            // 基本情報タブへ移動
            state.tab = tabBasic;
        }

        /**
         * 戻るボタン押下時の処理
         */
        function back() {
            router.back();
        }

        /**
         * ウィンドウサイズ変更監視
         * 変更されたウィンドウサイズを変数に保持する
         */
        function onResize(): void {
            state.windowSize = { x: window.innerWidth, y: window.innerHeight };
        }

        return {
            state: readonly(state),
            form,
            formOption,
            commonRef,
            materialPondRef,
            thumbnailPondRef,
            gradeTreeView,
            subjectTreeView,
            maxLength,
            titleRules,
            urlRules,
            gradeRules,
            subjectRules,
            nameRules,
            summaryRules,
            mailRules,
            searchKeywordRules,
            copyrightRules,
            operationEnvironmentRules,
            remarksRules,
            organizationIdRules,
            changeSchoolType,
            changeGradeItems,
            changeSubjectItems,
            updateMaterialFiles,
            updateThumbnailFiles,
            back,
            displayDialog,
            processingData,
            clearData,
            onResize
        };
    }
});
